<template>
  <ValidationObserver ref="interstitialFormValidator" v-slot="{ errors }">
    <form
      @submit.prevent="submitInterstitial"
      novalidate
      data-qa="por-interstitial-form">

      <!-- # Given Name -->
      <Input
        id="given_name"
        :input-model.sync="given_name"
        validation-rules="required|valid_name"
        :label-text="$t('interstitialForm_firstName_input')"
        :placeholder-text="$t('interstitialForm_placeholderFirstName_text')"
        input-type="text"
        :error-text="`${$t('interstitialForm_emptyFirstName_text')}.`"
        class="por-interstitial-form__field-wrapper"
        data-qa="por-interstitial-form__given_name" />

      <div class="is-size-7 por-interstitial--field-helper-text">
        <span data-qa="por-interstitial-form__email-bottom-info">{{ $t('interstitialForm_firstNameBottom_text') }}</span>
      </div>

      <!-- # Family Name -->
      <Input
        id="family_name"
        :input-model.sync="family_name"
        validation-rules="required|valid_name"
        :label-text="$t('interstitialForm_lastName_input')"
        :placeholder-text="$t('interstitialForm_placeholderLastName_text')"
        input-type="text"
        :error-text="`${$t('interstitialForm_emptyLastName_text')}.`"
        class="por-interstitial-form__field-wrapper mt-4"
        data-qa="por-interstitial-form__family_name" />

      <div class="is-size-7 por-interstitial--field-helper-text">
        <span data-qa="por-interstitial-form__email-bottom-info">{{ $t('interstitialForm_lastNameBottom_text') }}</span>
      </div>

      <!-- # Email -->
      <Input
        id="email"
        :input-model.sync="email"
        validation-rules="required|email"
        :label-text="$t('common_email_label')"
        :placeholder-text="$t('common_email_placeholder')"
        input-type="email"
        :error-text="`${$t('common_validEmail_text')}.`"
        disabled
        class="por-interstitial-form__field-wrapper mt-4"
        data-qa="por-interstitial-form__email" />

      <div class="is-size-7 por-interstitial--field-helper-text">
        <span data-qa="por-interstitial-form__email-bottom-info">{{ $t('interstitialForm_verifyEmail_text') }}</span>
      </div>

      <!-- # Birthday -->
      <div class="por-interstitial-form__field-wrapper mt-4 mb-4">
        <div class="Row">
          <label
            class="label z-u-text-label"
            for="birthdate"
            data-qa="por-interstitial-form__birthdate--label">
            {{ $t('interstitialForm_dob_label') }}
          </label>
          <div class="columns columns-no-margin">
            <div class="column column-no-padding">
              <ValidationProvider name="birthdate_day" rules="required" v-slot="{ errors }">
                <b-field
                  custom-class="z-u-text-label"
                  :type="{ 'is-danger': errors[0] }"
                  class="m-0">
                  <b-select
                    v-model="birthdate_day"
                    :aria-label="$t('interstitialForm_day_label')"
                    :placeholder="$t('interstitialForm_day_label')"
                    id="por-interstitial-form__birthdate--input-day"
                    name="por-interstitial-form__birthdate--input-day"
                    size="is-medium"
                    data-qa="por-interstitial-form__birthdate--input"
                    expanded
                    data-hj-suppress>
                    <option
                      v-for="day in getDaysInMonth()"
                      :key="`day-${day.number}`"
                      :value="day.number">
                      {{ day.value }}
                    </option>
                  </b-select>
                </b-field>
              </ValidationProvider>
            </div>
            <div class="column column-no-padding">
              <ValidationProvider name="birthdate_month" rules="required" v-slot="{ errors }">
                <b-field
                  custom-class="z-u-text-label"
                  :type="{ 'is-danger': errors[0] }"
                  class="m-0">
                  <b-select
                    v-model="birthdate_month"
                    :aria-label="$t('interstitialForm_month_select')"
                    :placeholder="$t('interstitialForm_month_select')"
                    id="por-interstitial-form__birthdate--input-month"
                    name="por-interstitial-form__birthdate--input-month"
                    size="is-medium"
                    data-qa="por-interstitial-form__birthdate--input"
                    expanded
                    data-hj-suppress>
                    <option
                      v-for="month in getMonthsInYear()"
                      :key="`month-${month.number}`"
                      :value="month.number">
                      {{ $t(month.value) }}
                    </option>
                  </b-select>
                </b-field>
              </ValidationProvider>
            </div>
            <div class="column column-no-padding">
              <ValidationProvider name="birthdate_year" rules="required" v-slot="{ errors }">
                <b-field
                  custom-class="z-u-text-label"
                  :type="{ 'is-danger': errors[0] }"
                  class="m-0">
                  <b-select
                    v-model="birthdate_year"
                    :aria-label="$t('interstitialForm_year_select')"
                    :placeholder="$t('interstitialForm_year_select')"
                    id="por-interstitial-form__birthdate--input-year"
                    name="por-interstitial-form__birthdate--input-year"
                    size="is-medium"
                    data-qa="por-interstitial-form__birthdate--input"
                    expanded
                    data-hj-suppress>
                    <option
                      v-for="year in getYears()"
                      :key="`year-${year}`"
                      :value="year.toString()">
                      {{ year }}
                  </option>
                  </b-select>
                </b-field>
              </ValidationProvider>
            </div>
          </div>
          <!-- The below 'errors' is coming from the form ValidationObserver, NOT the ValidationProvider -->
          <span
            class="por-input-error-text"
            data-qa="por-interstitial-form__birthdate_day--error"
            v-if="
              errors && errors['birthdate_day'] && !!errors['birthdate_day'].length ||
              errors && errors['birthdate_month'] && !!errors['birthdate_month'].length ||
              errors && errors['birthdate_year'] && !!errors['birthdate_year'].length
            ">
              {{ $t('interstitialForm_validDate_text') }}.
            </span>
        </div>
      </div>

      <div class="is-size-7 por-interstitial--field-helper-text">
        <span data-qa="por-interstitial-form__email-bottom-info">{{ $t('interstitialForm_dateOfBirthBottom_text') }}</span>
      </div>

      <!-- # Gender -->
      <fieldset
        class="por-interstitial-form__field-wrapper mt-4"
        data-qa="por-interstitial-form__gender--wrapper">
        <ValidationProvider rules="required" name="gender" v-slot="{ errors }">
          <div>
            <legend
              class="label z-u-text-label"
              data-qa="por-interstitial-form__gender--legend">
              {{ $t('interstitialForm_gender_legend') }}
            </legend>
            <div class="columns columns-no-margin">
              <div class="column column-no-padding">
                <b-radio
                  :class="{ 'por-input-field--error': !!errors.length }"
                  class="por-interstitial-form__input--wrapper-input pt-2 pr-2 pb-2 pl-2"
                  v-model="gender"
                  id="por-interstitial-form__gender_female"
                  native-value="f"
                  data-qa="por-interstitial-form__gender--input"
                  data-hj-suppress>
                  <span data-qa="por-interstitial-form__gender--label">
                    {{ $t('interstitialForm_genderFemale_radio') }}
                  </span>
                </b-radio>
              </div>
              <div class="column column-no-padding">
                <b-radio
                  :class="{ 'por-input-field--error': !!errors.length }"
                  class="por-interstitial-form__input--wrapper-input pt-2 pr-2 pb-2 pl-2"
                  v-model="gender"
                  id="por-interstitial-form__gender_male"
                  native-value="m"
                  data-qa="por-interstitial-form__gender--input"
                  data-hj-suppress>
                  <span data-qa="por-interstitial-form__gender--label">
                    {{ $t('interstitialForm_genderMale_label') }}
                  </span>
                </b-radio>
              </div>
            </div>
            <span
              v-if="!!errors.length"
              class="por-input-error-text"
              data-qa="por-interstitial-form__gender--error">
              {{ $t('interstitialForm_emptyOptions_text') }}.
            </span>
          </div>
        </ValidationProvider>
      </fieldset>

      <div v-if="isSuperdrug" class="por-interstitial-form__sd-wrapper pt-6 pb-6" data-qa="por-interstitial-form__sd-wrapper">
        <h3 class="z-u-text-label">Order communications</h3>
        <p class="por-interstitial-form__sd-text">Superdrug Online Doctor will contact you via email for order related purposes. No medical information will be in these messages.</p>
      </div>

      <!-- # Phone -->
      <PhoneInput
        :label-text="$t('interstitialForm_mobile_text')"
        id="phone"
        ref="phoneInput"
        v-model="phone_number"
        @isValid="updatePhoneValidity"
        class="por-interstitial-form__field-wrapper"
        data-qa="por-interstitial-form__phone_number"
        :key="phoneInputKey"
        :custom-error-message="true"
      >
        <template #helper-text>
          <span class="subtitle">{{ $t('interstitialForm_mobile_subtitle') }}</span>
        </template>
        <template v-if="!isPhoneValid" #error-info-text>
          <a @click="clearPhoneInput('interstitial')">{{ $t('interstitialForm_mobile_sms_clear') }}</a>
        </template>
      </PhoneInput>

      <ValidationProvider
        name="sms_options_selection"
        :rules="{ required_if:phone_number.length > 0 }"
        v-slot="{ errors }">
        <!-- Hidden checkbox required as only first checkbox is used for validation -->
        <b-checkbox style="display: none;" :value="(sms_updates || sms_offers)" />
        <div
          v-if="displaySmsOptions"
          class="por-interstitial--sms-modal mt-3 p-6"
          :class="{ 'por-input-field--error': errors[0] }">
          <span class="por-interstitial--sms-modal-header">{{ $t('interstitialForm_mobile_sms_heading')  }}</span>
          <b-checkbox
            class="por-interstitial--sms-modal-input pt-2 pr-2 pb-2 pl-2 mt-4 mb-2"
            :class="{ 'por-input-field--error': errors[0] }"
            name="smsUpdates"
            id="smsUpdates"
            :value="false"
            v-model="sms_updates"
            data-qa="form-sms_updates--input">
            <span>
              {{ $t('interstitialForm_mobile_sms_updates')  }}
            </span>
          </b-checkbox>
          <b-checkbox
            class="por-interstitial--sms-modal-input pt-2 pr-2 pb-2 pl-2 mt-2"
            :class="{ 'por-input-field--error': errors[0] }"
            name="smsOffers"
            id="smsOffers"
            :value="false"
            v-model="sms_offers"
            data-qa="form-sms_offers--input">
            <span>
              {{ $t('interstitialForm_mobile_sms_offers')  }}
            </span>
          </b-checkbox>
          <span
            v-if="!!errors.length"
            class="por-input-error-text">
            {{ $t('interstitialForm_mobile_sms_consent_error') }} <a @click="clearPhoneInput('interstitial')">{{ $t('interstitialForm_mobile_sms_clear') }}</a>
          </span>
        </div>
      </ValidationProvider>

      <div
        v-if="sms_cleared && phone_number.length < 1"
        class="por-interstitial--sms-confirm p-4">
        <strong>{{ $t('interstitialForm_mobile_sms_removed') }}</strong>
      </div>

      <div class="por-interstitial-form__optin-wrapper pt-6 pb-6" data-qa="por-interstitial-form__optin-wrapper">
        <h3 class="z-u-text-label">{{ $t('interstitialForm_marketing_text') }}</h3>
        <p class="por-interstitial-form__optin-text">{{ $t('interstitialForm_promotional_text') }}</p>
        <p class="por-interstitial-form__optin-text pt-6">{{ $t('interstitialForm_optOut_text') }}</p>
      </div>

      <!-- # Third party -->
      <fieldset v-if="thirdParty" data-qa="por-interstitial-form__third-party-field-wrapper"
        class="por-interstitial-form__field-wrapper">
        <legend class="por-interstitial-form__hidden" aria-hidden="false">
          {{ $t(`${thirdParty}_signupMarketing_text`) }}
        </legend>
        <div class="por-interstitial-form__third-party-wrapper" data-qa="por-interstitial-form__third-party-wrapper">
          <h3 class="z-u-text-label pb-1">{{ $t(`${thirdParty}_signupMarketing_text`) }}</h3>
          <b-field
            custom-class="z-u-text-label"
            :type="{ 'is-danger': errors[0] }">
            <b-checkbox
              class="por-interstitial-form__input--wrapper-input pt-2 pr-2 pb-2 pl-2"
              name="thirdParty"
              id="thirdParty"
              v-model="third_party_consent"
              data-qa="por-interstitial-form__third_party_consent--input">
              {{ $t(`${thirdParty}_receiveMarketingMails_text`) }}
            </b-checkbox>
          </b-field>
        </div>
      </fieldset>

      <!-- # T&C -->
      <fieldset
        class="por-interstitial-form__field-wrapper"
        data-qa="por-interstitial-form__terms_and_conditions--wrapper">
        <legend class="por-interstitial-form--hidden" aria-hidden="false">
          {{ $t('interstitialForm_termsConditions_text') }}
        </legend>
        <ValidationProvider name="terms_and_conditions" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
          <b-field
            custom-class="z-u-text-label"
            :label="$t('interstitialForm_termsConditions_text')"
            :message="[errors[0] ? `${$t('interstitialForm_termsAndConditions_text')}.` : '']"
            :type="{ 'is-danger': errors[0] }"
            >
            <b-checkbox
              class="por-interstitial-form__input--wrapper-input pt-2 pr-2 pb-2 pl-2"
              :class="{ 'por-input-field--error': errors[0] }"
              name="terms_and_conditions"
              id="por-interstitial-form__terms_and_conditions"
              :value="true"
              v-model="terms_and_conditions"
              data-qa="por-interstitial-form__terms_and_conditions--input">
              <span data-qa="por-interstitial-form__terms_and_conditions--input">
                {{ $t('interstitialForm_agreeMessage_checkbox') }} <a :href="tcLink" target="_blank" rel="noopener noreferrer">{{ $t('interstitialForm_termsConditions_text') }}</a> {{ $t('interstitialForm_Formterms_text') }} <a :href="privacyLink" target="_blank" rel="noopener noreferrer">{{ $t('common_privacyPolicy_text') }}</a><span v-if="isDe"> zu</span>.
              </span>
            </b-checkbox>
          </b-field>
        </ValidationProvider>
      </fieldset>

      <!-- # Submit Button -->
      <div class="por-interstitial-form__field-wrapper">
        <b-button
          size="is-medium"
          type="is-primary"
          expanded
          :disabled="isSubmitting"
          :loading="isSubmitting"
          tag="input"
          native-type="submit"
          :value="$t('common_register_text')"
          data-qa="por-interstitial-form__submit-button" />
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { Auth } from '@aws-amplify/auth'
import { mapGetters } from 'vuex'
import parseISO from 'date-fns/parseISO'
import { track, identify } from '@/services/tracking'
import { errorMessageMapping } from '@/helpers/message-mapping'
import consentMixin from '@/helpers/sms-consent-mixin'
import registerMixin from '@/helpers/registration-mixin'

import trackErrors from '@/helpers/track-errors'
import { redirect } from '@/services/redirect'
import { getDaysInMonth, getMonthsInYear, getYears } from '@/helpers/dates'
import PhoneInput from '@/components/inputs/phone-input/PhoneInput.vue'

import Input from '@/components/inputs/input'

export default {
  name: 'InterstitialForm',
  components: {
    Input,
    PhoneInput
  },
  mixins: [consentMixin, registerMixin],
  data () {
    return {
      email: '',
      given_name: '',
      family_name: '',
      birthdate_day: null,
      birthdate_month: null,
      birthdate_year: null,
      gender: '',
      phone_number: '',
      phoneInputKey: 0,
      isPhoneValid: false,
      showPasswordErrorText: false,
      sms_updates: false,
      sms_offers: false,
      sms_cleared: false,
      notifications: false,
      third_party_consent: false,
      terms_and_conditions: false,
      submitStatus: null,
      smsMessagesOptedOut: false
    }
  },
  computed: {
    ...mapGetters({
      tcLink: 'site/tcLink',
      privacyLink: 'site/privacyLink',
      brand: 'site/brand',
      actualLocale: 'site/actualLocale'
    }),
    formattedBirthdate: function () {
      let formatted = `${this.birthdate_year}-${this.birthdate_month}-${this.birthdate_day}T00:00:00Z`
      let parsed = parseISO(formatted)
      return parsed.toISOString().split('T')[0]
    }
  },
  watch: {
    sms_updates (newVal) {
      if (newVal) {
        this.trackSmsOptionSelection('updates', 'interstitial')
      }
    },
    sms_offers (newVal) {
      if (newVal) {
        this.trackSmsOptionSelection('offers', 'interstitial')
      }
    },
    phone_number: function (newVal) {
      if (newVal) {
        this.smsMessagesOptedOut = false
        this.sms_cleared = false
      } else {
        this.sms_offers = false
        this.sms_updates = false

        const errors = this.$refs.interstitialFormValidator.errors
        const smsOptionsError = errors && errors['sms_options_selection']

        if (
          smsOptionsError &&
          smsOptionsError.length > 0 &&
          !this.smsMessagesOptedOut
        ) {
          track({
            name: 'delete',
            params: {
              module: 'interstitial',
              description: `Phone number manually removed after no sms option error was displayed`
            }
          })
        }
      }
    }
  },
  mounted () {
    // Get patient names & email from current session and prefill inputs.
    Auth.currentAuthenticatedUser().then((res) => {
      const givenName = res.attributes.given_name
      // Google SSO placeholder; do not show or submit as the user's real name
      this.given_name = givenName === 'guest_google' ? '' : (givenName || '')
      this.family_name = res.attributes.family_name || ''
      this.email = res.attributes.email
    }).catch(() => {
      // User is logged out, redirect them to login
      this.$router.push({ name: 'login' })
    })
  },
  methods: {
    getDaysInMonth,
    getMonthsInYear,
    getYears,
    async submitInterstitial () {
      track({ name: 'button tap', params: { module: 'interstitial', description: 'Clicked register button' } })

      this.$refs.interstitialFormValidator.validate().then(async success => {
        const isNonEmptyPhone = this.phone_number.length > 0
        if (isNonEmptyPhone) {
          this.$refs.phoneInput.updateValidation()
        }
        const phoneInputError = isNonEmptyPhone ? !this.isPhoneValid : false
        const module = 'interstitial'

        if (!success || phoneInputError) {
          this.submitStatus = null
          trackErrors(this.$refs.interstitialFormValidator, 'interstitial')
          if (phoneInputError) {
            track({ name: `interstitial failed`, params: { description: 'phone', module } })
          }
          return
        }

        let consent = []

        if (this.sms_offers) consent.push('sms_marketing_consent')

        const requestPayload = {
          email: this.email,
          first_name: this.given_name.trim(),
          last_name: this.family_name.trim(),
          dob: `${this.birthdate_year}-${this.birthdate_month}-${this.birthdate_day}`,
          gender: this.gender,
          mobile: this.phone_number,
          notifications: this.sms_updates ? 'both' : 'email', // Value here is different to Auth.signUp call
          tc_consent: this.terms_and_conditions ? 1 : 0, // Value here is different to Auth.signUp call
          consent
        }

        this.submitStatus = 'PENDING' // disables submit button

        this.$axiosOrchestrator.put('/sso/patient', requestPayload)
          .then(() => {
            identify(undefined, {
              gender: requestPayload.gender,
              firstName: requestPayload.first_name,
              lastName: requestPayload.last_name
            })
            Auth.currentAuthenticatedUser({ bypassCache: true })
              .then(user => Auth.updateUserAttributes(user, {
                given_name: this.given_name.trim(),
                family_name: this.family_name.trim()
              }))
              .then(() => {
                this.submitStatus = 'OK'
                // track returns a non promise when segment isn't ready, hence in those cases convert it to promise for uniformity
                Promise.resolve(track({ name: 'success', params: { module: 'interstitial', description: 'Update additional info succeeded' } }))
                  .catch(() => {
                    // This is intentional since we don't need to do anything if track fails
                  }).finally(redirect)
              })
              .catch(error => {
                this.submitStatus = null
                if (window.newrelic) {
                  window.newrelic.noticeError(error)
                }
                // Logout the user to clear any broken session
                this.$router.push({ name: 'logout' })
              })
          })
          .catch(error => {
            this.submitStatus = null
            // axios orchestrator error handler already calls NewRelic hence passing false here
            let errorMessage = errorMessageMapping(error.message, false)
            this.$store.dispatch('site/setMessage', { message: errorMessage, type: 'danger' })
            track({ name: 'error', params: { module: 'interstitial', description: 'Update additional info failed' } })
          })
      })
    },
    updatePhoneValidity (event) {
      this.isPhoneValid = event
    }
  }
}
</script>

<style lang="scss" scoped>
  $por-interstitial-form--column-tablet-margin-top: 8px;
  $por-interstitial-form--field-wrapper-margin-top: 16px;

  // To match Bulma styles
  $por-interstitial-form-input--border: 1px solid #D0CFCB;
  $por-interstitial-form-input--border-radius: 8px;

  .por-interstitial-form__field-wrapper {
    display: block;
  }

  .por-interstitial-form__field-wrapper__margin-top,
  .por-interstitial-form__field-wrapper {
    margin-top: $por-interstitial-form--field-wrapper-margin-top;
  }

  .por-interstitial-form__input--wrapper-input {
    display: flex;
    align-items: baseline;
    width: 100%;
    border: $por-interstitial-form-input--border;
    border-radius: $por-interstitial-form-input--border-radius;
  }

  .b-checkbox.checkbox.por-interstitial-form-input--wrapper-input { // Bulma override
    align-items: flex-start;
  }

  .por-interstitial-form--hidden {
    display: none;
  }

  .subtitle {
    font-size: 16px;
    font-weight: 400;
    color: #4a4a4a;
  }

  @media(max-width: 768px) {
    .column + .column {
      margin-top: $por-interstitial-form--column-tablet-margin-top;
    }
  }

  .por-interstitial--field-helper-text {
    margin-top: -8px;
  }

  .por-interstitial--sms-modal {
    background: #f5f6f9;
    border-radius: 8px;

    .por-interstitial--sms-modal-header {
      display: flex;
      font-weight: bold;
      gap: 8px;

      &::before {
        content: url('data:image/svg+xml,<svg width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.3334 16.4168H1.66675L1.83285 16.1677C3.15905 14.1784 3.86675 12.2077 3.86675 9.81683V8.71683C3.86675 5.88177 6.16502 3.5835 9.00008 3.5835C11.8351 3.5835 14.1334 5.88177 14.1334 8.71683V9.81683C14.1334 12.2077 14.8411 14.1784 16.1673 16.1677L16.3334 16.4168Z" stroke="%230E1661" stroke-width="1.83333" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.75 16.4165C11.75 17.9353 10.5188 19.1665 9 19.1665C7.48122 19.1665 6.25 17.9353 6.25 16.4165" stroke="%230E1661" stroke-width="1.83333"/><path d="M9 3.58333V1.75" stroke="%230E1661" stroke-width="1.83333" stroke-linecap="round"/></svg>');
        margin-top: 2px;
      }
    }

    .por-interstitial--sms-modal-input {
      background-color: #fff;
      border: 1px solid #D0CFCB;
      border-radius: 8px;
      width: 100%;
    }
  }

  .por-interstitial--sms-confirm {
    background-color: #F2F8F6;
    border: 2px solid #007044;
    border-radius: 8px;

    strong {
      color: #007044;
      display: flex;
      gap: 8px;

      &::before {
        content: url('data:image/svg+xml,<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18.293 6.29297C18.6835 5.90244 19.3165 5.90244 19.707 6.29297C20.0976 6.68349 20.0976 7.31651 19.707 7.70703L9.70703 17.707C9.31651 18.0976 8.68349 18.0976 8.29297 17.707L4.29297 13.707C3.90244 13.3165 3.90244 12.6835 4.29297 12.293C4.68349 11.9024 5.31651 11.9024 5.70703 12.293L9 15.5859L18.293 6.29297Z" fill="%23007044"/></svg>');
        margin-top: 2px;
        max-height: 20px;
      }
    }
  }
</style>
